<template>
  <div
    class="user-card"
    @click="viewUser"
    @mouseover="hoverEffect"
    @mouseleave="removeHoverEffect"
  >
    <b-card tag="article" class="mb-2 card-color user-card-rect">
      <b-form-checkbox
        class="select-checkbox"
        :checked="selected"
        @change="$emit('toggleSelect', userID)"
        @click.native.stop
      ></b-form-checkbox>

      <!-- View Button at the Top Right -->
      <div class="card-actions top-right">
        <b-button
          class="view-button"
          href="#"
          variant="primary"
          v-b-modal="'view-modal-' + userID"
        >
          View
        </b-button>
      </div>

      <!-- User Details -->
      <div class="user-details">
        <b-card-text>
          <span class="username">{{ userName }}</span
          ><br />
          <span>{{ email }}</span
          ><br />
          <span class="user-type">{{ userType }}</span>
        </b-card-text>
      </div>

      <!-- Buttons at the Bottom Right -->
      <div class="button-group bottom-right">
        <b-button
          class="message-button"
          href="#"
          variant="secondary"
          @click.stop="$emit('messageUser', userID)"
        >
          Message
        </b-button>
        <b-button
          class="edit-button"
          href="#"
          variant="warning"
          v-b-modal="'edit-modal-' + userID"
          @click.stop
        >
          Edit
        </b-button>
        <b-button
          class="delete-button"
          href="#"
          variant="danger"
          v-b-modal="'delete-modal-' + userID"
          @click.stop
        >
          Delete
        </b-button>
      </div>
    </b-card>

    <!-- View Modal -->
    <b-modal :id="'view-modal-' + userID" title="View User Details">
      <template #modal-header="{ close }">
        <h5 class="modal-title text-orange">View User Details</h5>
        <b-button variant="outline-danger" size="sm" @click="close">X</b-button>
      </template>
      <ViewUserDialog
        :userName="userName"
        :email="email"
        :userType="userType"
        :nickName="nickName"
        :bio="bio"
        :gender="gender"
        :games="games"
      />
      <template #modal-footer="{ ok }">
        <div class="center-footer">
          <b-button class="custom-ok-button" @click="ok">OK</b-button>
        </div>
      </template>
    </b-modal>

    <!-- Edit Modal -->
    <b-modal :id="'edit-modal-' + userID" title="Edit User Details" hide-footer>
      <template #modal-header="{ close }">
        <h5 class="modal-title text-orange">Edit User Details</h5>
        <b-button variant="outline-danger" size="sm" @click="close">X</b-button>
      </template>
      <EditUserDialog
        :userName="userName"
        :email="email"
        :userType="userType"
        :nickName="nickName"
        :bio="bio"
        :gender="gender"
        @save="saveChanges"
        @cancel="$bvModal.hide('edit-modal-' + userID)" 
      />
      <template #modal-footer="{ cancel }">
        <b-button class="custom-cancel-button" @click="cancel">Cancel</b-button>
      </template>
    </b-modal>

    <!-- Delete Modal -->
    <b-modal
      :id="'delete-modal-' + userID"
      title="Confirm Deletion"
      @ok="confirmDelete(userID)"
    >
      <template #modal-header="{ close }">
        <h5 class="modal-title text-danger">Confirm Deletion</h5>
        <b-button variant="outline-danger" size="sm" @click="close">X</b-button>
      </template>
      <p>Are you sure you want to delete this user?</p>
      <template #modal-footer="{ cancel, ok }">
        <b-button variant="danger" @click="ok">Delete</b-button>
        <b-button variant="outline-secondary" @click="cancel">Cancel</b-button>
      </template>
    </b-modal>

    <!-- Success Modal for Saving Changes -->
    <b-modal
      id="save-success-modal"
      title="Success"
      hide-header-close
      hide-footer
    >
      <p class="my-4">Changes have been saved successfully.</p>
      <b-button
        class="mt-2 orange-button"
        @click="hideSuccessModal('save-success-modal')"
        >OK</b-button
      >
    </b-modal>
  </div>
</template>

<script>
import ViewUserDialog from "./ViewUserDialog.vue";
import EditUserDialog from "./EditUserDialog.vue";

export default {
  name: "UserItem",
  components: {
    ViewUserDialog,
    EditUserDialog
  },
  props: {
    userID: { type: [String, Number], required: true },
    userName: { type: String, required: true },
    email: { type: String, required: true },
    userType: { type: String, required: true },
    nickName: { type: String, default: "No nickname provided" },
    bio: { type: String, default: "No bio provided" },
    gender: { type: String, default: "Gender not specified" },
    games: { type: Array, default: () => [] },
    selected: { type: Boolean, default: false }
  },
  methods: {
    saveChanges(updatedUser) {
  console.log("Saving changes for user:", updatedUser);
  this.$emit("saveUser", {
    userID: this.userID,
    ...updatedUser, // Include all updated fields
  });

  // Show success modal
  this.$bvModal.hide("edit-modal-" + this.userID);
  this.$bvModal.show("save-success-modal");
},

    viewUser() {
      this.$bvModal.show("view-modal-" + this.userID);
    },
    confirmDelete(userID) {
      this.$emit("removeUser", userID);
      this.$bvModal.show("delete-success-modal");
    },
    hideSuccessModal(modalID) {
      this.$bvModal.hide(modalID);
    },
    hoverEffect(event) {
      event.currentTarget.style.cursor = "pointer";
    },
    removeHoverEffect(event) {
      event.currentTarget.style.cursor = "default";
    }
  }
};
</script>

<style scoped>
.user-card {
  display: center;
  flex-direction: column;
  width: 350px;
  margin-bottom: 1px;
  margin-left: 20px;
  margin-right: 20px;
  transition: transform 0.2s;
}
.user-card:hover {
  transform: scale(1.02);
}

.user-card-rect {
  position: relative;
  background-color: white;
  border-radius: 0.8rem;
  padding: 1px;
  min-height: 120px;
}

.top-right {
  position: absolute;
  top: 5px;
  right: 5px;
}

.card-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.select-checkbox {
  position: absolute;
  top: 17px;
  left: 12px;
  z-index: 1;
}

.user-details {
  padding-left: 24px;
}

.bottom-right {
  position: absolute;
  bottom: 5px;
  right: 5px;
  display: flex;
}

.username {
  color: #f97427;
  font-weight: bold;
  font-size: 1rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-type {
  font-size: 0.9rem;
  color: #6c757d;
  font-style: italic;
}

.view-button,
.edit-button,
.delete-button,
.message-button {
  font-size: 0.75rem;
  padding: 6px 10px;
  border: none;
  color: white;
}

.message-button {
  background-color: #6c757d !important;
  margin-right: 5px;
}

.message-button:hover {
  background-color: #565e64 !important;
}

.delete-button {
  background: #d83b3b !important;
  margin-left: 5px;
}

.view-button {
  background-color: #f89d53 !important;
}

.edit-button {
  background-color: #f97427 !important;
}

.delete-button:hover {
  background-color: #c50c0c !important;
}

.view-button:hover {
  background-color: #d47d39 !important;
}

.edit-button:hover {
  background-color: #d45f1e !important;
}

.custom-save-button,
.custom-ok-button {
  background-color: #f97427;
  color: white;
  border: none;
}

.modal-header button {
  margin-left: auto;
}
</style>



// WEBPACK FOOTER //
// src/components/admin-only/UserItem.vue